/**
 * reveal.js — Amplitude theme scroll-reveal & animation engine.
 *
 * Capabilities:
 *  1. Scroll reveal: [data-reveal] → .is-visible (IntersectionObserver).
 *     CSS variants keyed off the attribute value (animations.css handles them).
 *  2. Stagger groups: [data-reveal-stagger] containers cascade children.
 *  3. Count-up: [data-count] elements animate their numeric value.
 *  4. Parallax: [data-parallax] elements receive a gentle vertical shift.
 *
 * Progressive enhancement: every feature is a no-op if the API is absent.
 * Dependency-free. Respects prefers-reduced-motion throughout.
 */
(function () {
  'use strict';

  /* ─── Feature guards ─────────────────────────────────────────────────── */
  var hasIO = ('IntersectionObserver' in window);
  var prefersReducedMotion = (
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  /* ─── 1. SCROLL REVEAL ──────────────────────────────────────────────── */
  if (hasIO) {
    document.documentElement.classList.add('js-reveal');

    var revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var el = entry.target;

          /* Check if this element is a direct child of a stagger container
             that hasn't fired yet — the container observer handles those. */
          if (el.closest('[data-reveal-stagger]') &&
              !el.hasAttribute('data-reveal-stagger')) {
            return; /* handled by stagger logic */
          }

          el.classList.add('is-visible');
          revealObserver.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    /* Stagger container observer */
    var staggerObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var container = entry.target;
          var children = container.querySelectorAll('[data-reveal]');
          var baseDelay = parseInt(container.getAttribute('data-reveal-stagger-delay') || '80', 10);

          children.forEach(function (child, index) {
            var delay = index * baseDelay;
            child.style.setProperty('--reveal-delay', delay + 'ms');

            /* Small timeout so CSS paint + custom prop are applied first */
            setTimeout(function () {
              child.classList.add('is-visible');
            }, prefersReducedMotion ? 0 : delay);
          });

          staggerObserver.unobserve(container);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.04 }
    );

    function observeAll() {
      /* Stagger containers first — their children must NOT go to revealObserver */
      document.querySelectorAll('[data-reveal-stagger]').forEach(function (container) {
        staggerObserver.observe(container);
      });

      /* Plain reveal elements that are NOT inside a stagger container */
      document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach(function (el) {
        if (!el.closest('[data-reveal-stagger]')) {
          revealObserver.observe(el);
        }
      });
    }

    if (document.readyState !== 'loading') {
      observeAll();
    } else {
      document.addEventListener('DOMContentLoaded', observeAll);
    }

    /* Re-scan after Shopify theme editor injects/reloads sections */
    document.addEventListener('shopify:section:load', observeAll);
  } else {
    /* Fallback: no IntersectionObserver — make everything visible immediately */
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('[data-reveal]').forEach(function (el) {
        el.classList.add('is-visible');
      });
    });
  }


  /* ─── 2. COUNT-UP ───────────────────────────────────────────────────── */
  (function () {
    if (!hasIO) return;

    function formatNumber(value, prefix, suffix, separator) {
      var formatted = Math.round(value).toString();
      if (separator) {
        /* Insert thousands separators */
        formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      }
      return (prefix || '') + formatted + (suffix || '');
    }

    function animateCount(el) {
      var target = parseInt(el.getAttribute('data-count'), 10);
      if (isNaN(target)) return;

      var prefix    = el.getAttribute('data-count-prefix') || '';
      var suffix    = el.getAttribute('data-count-suffix') || '';
      var separator = el.getAttribute('data-count-separator');
      /* data-count-separator="" means no separator; omitting attr → auto ',' */
      if (separator === null) separator = ',';

      /* Under reduced motion: just show final value */
      if (prefersReducedMotion) {
        el.textContent = formatNumber(target, prefix, suffix, separator);
        return;
      }

      var duration = parseInt(el.getAttribute('data-count-duration') || '1400', 10);
      var startTime = null;
      var startValue = 0;

      el.classList.add('is-counting');

      function step(timestamp) {
        if (!startTime) startTime = timestamp;
        var elapsed = timestamp - startTime;
        var progress = Math.min(elapsed / duration, 1);
        /* Ease-out cubic */
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = startValue + (target - startValue) * eased;

        el.textContent = formatNumber(current, prefix, suffix, separator);

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          el.textContent = formatNumber(target, prefix, suffix, separator);
          el.classList.remove('is-counting');
        }
      }

      requestAnimationFrame(step);
    }

    var countObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          animateCount(entry.target);
          countObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -5% 0px', threshold: 0.2 }
    );

    function observeCounts() {
      document.querySelectorAll('[data-count]').forEach(function (el) {
        countObserver.observe(el);
      });
    }

    if (document.readyState !== 'loading') {
      observeCounts();
    } else {
      document.addEventListener('DOMContentLoaded', observeCounts);
    }

    document.addEventListener('shopify:section:load', observeCounts);
  }());


  /* ─── 3. PARALLAX ───────────────────────────────────────────────────── */
  (function () {
    /* Skip entirely under reduced motion or if rAF unavailable */
    if (prefersReducedMotion) return;
    if (!window.requestAnimationFrame) return;

    var parallaxEls = [];
    var ticking = false;

    function gatherParallaxEls() {
      parallaxEls = Array.prototype.slice.call(
        document.querySelectorAll('[data-parallax]')
      );
    }

    function applyParallax() {
      var scrollY = window.scrollY || window.pageYOffset;

      parallaxEls.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var viewH = window.innerHeight;

        /* Only transform elements close to the viewport */
        if (rect.bottom < -viewH || rect.top > viewH * 2) return;

        /* strength: how many px to travel per 100px scroll (default 8) */
        var strength = parseFloat(el.getAttribute('data-parallax') || '8');
        /* Offset relative to element centre passing viewport midpoint */
        var elCentre = rect.top + rect.height / 2;
        var vpCentre = viewH / 2;
        var distance = elCentre - vpCentre;
        var shift = (distance / viewH) * strength * -1;

        el.style.transform = 'translateY(' + shift.toFixed(2) + 'px)';
      });

      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(applyParallax);
        ticking = true;
      }
    }

    function init() {
      gatherParallaxEls();
      if (parallaxEls.length === 0) return;
      window.addEventListener('scroll', onScroll, { passive: true });
      applyParallax(); /* initial position */
    }

    if (document.readyState !== 'loading') {
      init();
    } else {
      document.addEventListener('DOMContentLoaded', init);
    }

    /* Re-gather on section reload */
    document.addEventListener('shopify:section:load', function () {
      gatherParallaxEls();
    });
  }());

}());
